import type { ReactNode } from 'react'
import { Navigate, Outlet, useLocation, useNavigate, useOutlet } from 'react-router-dom'
import { PageHeader } from '@/components/layout/PageHeader'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { childSurfaces, type SurfaceEntry } from '@/custom'

/**
 * Shell for every primary module surface (SA-323 §7.1). Fleet in name only:
 * App.tsx hands it every primary parent, with or without enabled children
 * (SA-1125). It always renders the title band — the surface's one h1 through
 * PageHeader, with the nav tag beside it (Analytics, Experimental) — and the
 * basis Tabs strip under the title only when more than one child is enabled and
 * permitted; the child pages write no title of their own. Every tab is a route:
 * deep-linkable, bookmarkable; a child page's own query parameters (a study's
 * `?study=`) keep its tab active.
 */
export function FleetShell({ entry }: { entry: SurfaceEntry }) {
  const isEnabled = useFeatureStore((s) => s.isEnabled)
  const location = useLocation()
  const navigate = useNavigate()
  // Every primary surface routes as `${route}/*` (SA-1125), so an address no child route
  // answers — a stale link to a tab whose flag is off — still lands here with no outlet.
  const outlet = useOutlet()
  // One fixed pair of reads whatever `entry` is (the useNavModel shape): React Router
  // renders a matched route without a key, so going from Fleet to Route studies keeps
  // this instance and only changes `entry`. A hook per child changed the hook count
  // there (one child, then two): React error #310. These selectors are the
  // subscription; `hasPermission` keeps the store's "granted until loaded" rule.
  usePermissionStore((s) => s.isLoaded)
  usePermissionStore((s) => s.permissions)
  const { hasPermission } = usePermissionStore.getState()
  const children = childSurfaces(entry.id)
  const permitted = children.map((c) => hasPermission(c.permission))
  const tabs = [{ id: entry.id, route: entry.route, label: entry.tab?.label ?? entry.nav?.label ?? entry.id }].concat(
    children.filter((c, i) => isEnabled(c.feature) && permitted[i]).map((c) => ({ id: c.id, route: c.route, label: c.tab?.label ?? c.id })),
  )
  const active = [...tabs].reverse().find((t) => location.pathname === t.route || location.pathname.startsWith(t.route + '/'))?.route ?? entry.route
  // The shell takes the AppShell main's padding for itself (negative margins, and a
  // height that adds the padding back — `h-full` alone is the content box, which left
  // the page 48 px short of the bottom) and hands the SAME padding back around the
  // outlet, so a child page sees exactly the box it would get without tabs and never
  // has to know the shell exists. A full-bleed child (the SA-321 locator) then reaches
  // the strip's bottom edge and stops — with the outlet unpadded its negative top
  // margin pulled its own header row up behind the strip.
  // `gap-0` cancels the basis Tabs root's default `gap-2` — the strip and the panel meet edge to edge.
  const shell = '-mx-4 -my-4 flex h-[calc(100%+2rem)] flex-col gap-0 lg:-mx-6 lg:-my-6 lg:h-[calc(100%+3rem)]'
  const panel = 'min-h-0 flex-1 p-4 lg:p-6'
  // SA-1125: the title band re-adds the main's padding above the title, so the title sits
  // where a page's own would, and keeps D1's stacking context (relative + z-10 + opaque).
  if (outlet === null) return <Navigate to={entry.route} replace />
  const titleBand = (strip: ReactNode) => (
    <div data-slot="shell-title-band" className="relative z-10 bg-background px-4 pt-4 lg:px-6 lg:pt-6">
      <PageHeader title={entry.nav?.label ?? entry.id} tag={entry.nav?.tag}>{strip}</PageHeader>
    </div>
  )
  if (tabs.length <= 1) {
    return <div className={shell}>{titleBand(null)}<div className={panel}><Outlet /></div></div>
  }
  // SA-330 item 9: the outlet renders inside a TabsContent so the strip's triggers control a
  // real tabpanel (aria-controls / aria-labelledby) instead of sitting outside the Tabs tree.
  // The Tabs root IS the shell container; the title band holds the strip and owns a
  // stacking context (D1: relative + z-10 + opaque background above a full-bleed child).
  return (
    <Tabs value={active} onValueChange={(route) => navigate(route)} className={shell}>
      {titleBand(
        <TabsList aria-label={`${entry.nav?.label ?? entry.id} sections`}>
          {tabs.map((t) => (
            <TabsTrigger key={t.id} value={t.route} data-tour-nav={t.route}>{t.label}</TabsTrigger>
          ))}
        </TabsList>,
      )}
      <TabsContent value={active} className={panel}>
        <Outlet />
      </TabsContent>
    </Tabs>
  )
}

export default FleetShell
